import { useEffect, useMemo, useState } from 'react';
import {
  engagementMetricsApi,
  type EventForLogDto,
  type LogAttendanceResponseDto,
} from '../../../api/engagementMetrics';
import { EventSelect } from '../../../components/shared/EventSelect';

interface LogAttendanceModalProps {
  open: boolean;
  preselectEventId?: number | null;
  onClose: () => void;
  onSaved: (resp: LogAttendanceResponseDto) => void;
}

// formatShortDate function - unused but kept for reference
// function _formatShortDate(iso: string): string {
//   const d = new Date(iso);
//   if (Number.isNaN(d.getTime())) return iso;
//   return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
// }

export function LogAttendanceModal({ open, preselectEventId, onClose, onSaved }: LogAttendanceModalProps) {
  const [events, setEvents] = useState<EventForLogDto[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(preselectEventId ?? null);
  const [meetingDate, setMeetingDate] = useState<string>('');
  const [meetingOrganizer, setMeetingOrganizer] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [attendees, setAttendees] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingEvents(true);
    setError(null);
    engagementMetricsApi
      .eventsForLog()
      .then(resp => {
        if (cancelled) return;
        setEvents(resp.events);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingEvents(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Pre-fill attendees from existing attendance when event changes (AC14a)
  const selectedEvent = useMemo(
    () => events.find(e => e.eventId === selectedEventId) ?? null,
    [events, selectedEventId],
  );

  useEffect(() => {
    if (selectedEvent?.existingAttendance) {
      setAttendees(String(selectedEvent.existingAttendance.actualAttendees));
    } else if (selectedEvent) {
      setAttendees('');
    }
    // Populate fields from selected event
    if (selectedEvent) {
      setMeetingDate(selectedEvent.eventDate);
      setMeetingOrganizer(selectedEvent.eventOrganizer ?? '');
      setLocation(selectedEvent.eventLocation);
    } else {
      setMeetingDate('');
      setMeetingOrganizer('');
      setLocation('');
    }
  }, [selectedEvent]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSelectedEventId(preselectEventId ?? null);
      setAttendees('');
      setMeetingDate('');
      setMeetingOrganizer('');
      setLocation('');
      setError(null);
    }
  }, [open, preselectEventId]);

  useEffect(() => {
    if (!open) return;
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const dropdownEmpty = !loadingEvents && events.length === 0;
  const attendeesNum = Number(attendees);
  const attendeesValid = attendees !== '' && Number.isInteger(attendeesNum) && attendeesNum >= 0;
  const canSave = !saving && !!selectedEventId && attendeesValid && !dropdownEmpty;

  async function handleSave() {
    if (!selectedEventId || !attendeesValid) return;
    setSaving(true);
    setError(null);
    try {
      const resp = await engagementMetricsApi.logAttendance({
        eventId: selectedEventId,
        actualAttendees: attendeesNum,
      });
      onSaved(resp);
    } catch (e) {
      const err = e as Error;
      setError(err.message ?? 'Couldn\'t save attendance. Try again.');
    } finally {
      setSaving(false);
    }
  }

  // const _memberDenominator = selectedEvent?.currentMemberCount ?? 0;
  // const _previewPercent =
  //   _memberDenominator > 0 && attendeesValid
  //     ? ((_attendeesNum / _memberDenominator) * 100).toFixed(1)
  //     : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Log Attendance"
      data-testid="log-attendance-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          width: '460px',
          maxWidth: '95vw',
          borderRadius: '6px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
      >
        {/* Blue header */}
        <div style={{ background: '#2563eb', color: 'white', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Log Attendance</h2>
          <button
            aria-label="Close"
            data-testid="log-attendance-close"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'white',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Form content */}
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Event */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Event
              </label>
              <EventSelect
                value={selectedEventId}
                onChange={setSelectedEventId}
                events={events}
                disabled={loadingEvents || (!loadingEvents && events.length === 0)}
                placeholder="-- Select an event --"
              />
            </div>

            {/* Meeting Date */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Meeting Date
              </label>
              <input
                type="text"
                placeholder="mm/dd/yyyy"
                value={meetingDate}
                readOnly
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '13px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  background: '#f9fafb',
                  color: '#6b7280',
                }}
              />
            </div>

            {/* Meeting Organizer */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Meeting Organizer
              </label>
              <input
                type="text"
                value={meetingOrganizer}
                readOnly
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '13px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  background: '#f9fafb',
                  color: '#6b7280',
                }}
              />
            </div>

            {/* Location / Platform */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Location / Platform
              </label>
              <input
                type="text"
                value={location}
                readOnly
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '13px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  background: '#f9fafb',
                  color: '#6b7280',
                }}
              />
            </div>

            {/* No. Attendees */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                No. Attendees
              </label>
              <input
                type="number"
                min={0}
                step={1}
                data-testid="log-attendance-attendees"
                value={attendees}
                onChange={e => setAttendees(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '13px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                }}
              />
            </div>

            {error && (
              <div role="alert" data-testid="log-attendance-error" style={{ color: '#dc2626', fontSize: '12px' }}>
                {error}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
            <button
              onClick={onClose}
              data-testid="log-attendance-cancel"
              style={{
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                padding: '8px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={!canSave}
              data-testid="log-attendance-save"
              style={{
                background: canSave ? '#2563eb' : '#9ca3af',
                color: 'white',
                border: 'none',
                padding: '8px 20px',
                borderRadius: '4px',
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontSize: '13px',
                fontWeight: '500',
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
